import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { api } from '../api/client';
import { SkeletonBar, Button } from '../components/ui';

import { T } from '../styles/tokens';
interface QueueData {
  pending: number;
  processing: number;
  recent: any[];
}

interface QueueStatus {
  summary: {
    total_pending: number;
    total_processing: number;
    total_completed_24h: number;
    total_failed_24h: number;
  };
  queues: {
    installations: QueueData;
    reinstallations: QueueData;
    backups: QueueData;
    agent_creation: QueueData;
    credential_import: QueueData;
    skill_install: QueueData;
    machine_actions: QueueData;
    brain_saves: QueueData;
    credential_saves: QueueData;
  };
}

interface CronConfigItem {
  schedule: string | null;
  label: string;
  enabled: boolean;
  paused: boolean;
}

type CronConfig = Record<string, CronConfigItem>;

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  pending: { bg: T.warningBg, color: T.warning },
  processing: { bg: T.primaryBg, color: T.primary },
  running: { bg: T.accentBg, color: T.purple },
  completed: { bg: T.successBg, color: T.success },
  done: { bg: T.successBg, color: T.success },
  failed: { bg: 'rgba(248,113,113,0.18)', color: T.danger },
  expired: { bg: 'rgba(156,163,175,0.18)', color: T.textSecondary },
  partial: { bg: T.warningBg, color: T.warning },
  rejected: { bg: T.borderHover, color: T.textSecondary },
  cancelled: { bg: T.borderHover, color: T.textSecondary },
};

const QUEUE_CONFIG: { key: keyof QueueStatus['queues']; label: string }[] = [
  { key: 'installations', label: 'Instalacoes' },
  { key: 'reinstallations', label: 'Reinstalacoes' },
  { key: 'backups', label: 'Backups' },
  { key: 'agent_creation', label: 'Criacao de Agentes' },
  { key: 'credential_import', label: 'Importacao de Credenciais' },
  { key: 'skill_install', label: 'Instalacao de Skills' },
  { key: 'machine_actions', label: 'Acoes de Maquinas' },
 { key: 'brain_saves', label: 'Cerebro (Salvar Arquivos)' },
  { key: 'credential_saves', label: 'Edicao/Adicao de Credenciais' },
];

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }}>
      {status}
    </span>
  );
}

function CountBadge({ count, color }: { count: number; color: string }) {
  if (!count) return null;
  return (
    <span style={{
      padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 700,
      background: `${color}22`, color, marginLeft: 6,
    }}>
      {count}
    </span>
  );
}

function shortId(id: string) {
  return id ? id.substring(0, 8) : '-';
}

function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function retryLabel(retry_count: any, max_retries: any): string {
  if (retry_count == null && max_retries == null) return '-';
  return `${retry_count ?? 0}/${max_retries ?? '?'}`;
}

function parseIntegrationsCount(integrations: any): string {
  if (!integrations) return '-';
  try {
    const arr = typeof integrations === 'string' ? JSON.parse(integrations) : integrations;
    if (Array.isArray(arr)) return `${arr.length} integ.`;
    return String(integrations).substring(0, 20);
  } catch { return String(integrations).substring(0, 20); }
}

function StageBadge({ stage }: { stage: string | null | undefined }) {
  if (!stage) return <span>-</span>;
  return (
    <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: T.purpleBg, color: T.purple }}>
      {stage}
    </span>
  );
}

// --- Progress Bar ---

interface ProgressStep {
  key: string;
  label: string;
}

const INSTALLATION_STEPS: ProgressStep[] = [
  { key: 'pending', label: 'Na fila' },
  { key: 'preparing', label: 'Preparando' },
  { key: 'hostinger_recreate', label: 'Recriando VM' },
  { key: 'vm_provisioning', label: 'Provisionando' },
  { key: 'completed', label: 'Concluído' },
];

const BACKUP_STEPS: ProgressStep[] = [
  { key: 'pending', label: 'Na fila' },
  { key: 'processing', label: 'Processando' },
  { key: 'completed', label: 'Concluído' },
];

const AGENT_CREATION_STEPS: ProgressStep[] = [
  { key: 'pending', label: 'Na fila' },
  { key: 'processing', label: 'Processando' },
  { key: 'completed', label: 'Concluído' },
];

const CREDENTIAL_IMPORT_STEPS: ProgressStep[] = [
  { key: 'pending', label: 'Na fila' },
  { key: 'processing', label: 'Importando' },
  { key: 'completed', label: 'Concluído' },
];

const MACHINE_ACTION_STEPS: ProgressStep[] = [
  { key: 'pending', label: 'Na fila' },
  { key: 'running', label: 'Executando' },
  { key: 'done', label: 'Concluido' },
];

const QUEUE_STEPS: Record<string, ProgressStep[]> = {
  installations: INSTALLATION_STEPS,
  reinstallations: INSTALLATION_STEPS,
  backups: BACKUP_STEPS,
  agent_creation: AGENT_CREATION_STEPS,
  credential_import: CREDENTIAL_IMPORT_STEPS,
  skill_install: CREDENTIAL_IMPORT_STEPS,
  machine_actions: MACHINE_ACTION_STEPS,
  brain_saves: MACHINE_ACTION_STEPS,
};

function getStepForItem(queueKey: string, item: any): string {
  if (queueKey === 'installations' || queueKey === 'reinstallations') {
    return item.installation_stage || item.status || 'pending';
  }
  if (queueKey === 'agent_creation') {
    return item.stage || item.status || 'pending';
  }
  return item.status || 'pending';
}

function getStepLabel(queueKey: string, item: any, step: ProgressStep): string {
  if (queueKey === 'agent_creation' && step.key === 'processing' && item.stage_message) {
    return item.stage_message;
  }
  return step.label;
}

function ProgressBar({ steps, currentStep, status }: { steps: ProgressStep[]; currentStep: string; status: string }) {
  const isFailed = status === 'failed';
  const isCompleted = status === 'completed';
  let currentIdx = steps.findIndex(s => s.key === currentStep);
  if (currentIdx === -1) {
    // If current step not in list, try to map by status
    if (isCompleted) currentIdx = steps.length - 1;
    else if (status === 'processing') currentIdx = steps.findIndex(s => s.key === 'processing');
    else currentIdx = 0;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0, width: '100%', padding: '8px 0' }}>
      {steps.map((step, i) => {
        let dotColor = T.bgInput;
        let barColor = T.bgInput;
        let labelColor = T.textDisabled;
        let pulse = false;

        if (isCompleted) {
          dotColor = T.success;
          barColor = T.success;
          labelColor = T.success;
        } else if (i < currentIdx) {
          dotColor = T.success;
          barColor = T.success;
          labelColor = T.textMuted;
        } else if (i === currentIdx) {
          dotColor = isFailed ? T.danger : T.primary;
          barColor = isFailed ? T.danger : T.primary;
          labelColor = isFailed ? T.danger : T.primary;
          if (!isFailed) pulse = true;
        }

        return (
          <div key={step.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
            {/* Bar + Dot row */}
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {/* Left bar segment */}
              {i > 0 && (
                <div style={{
                  flex: 1, height: 3,
                  background: (isCompleted || i <= currentIdx) ? T.success : T.bgInput,
                  borderRadius: i === 0 ? '2px 0 0 2px' : 0,
                }} />
              )}
              {i === 0 && <div style={{ flex: 1 }} />}
              {/* Dot */}
              <div style={{
                width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0,
                boxShadow: pulse ? `0 0 6px ${dotColor}` : 'none',
                animation: pulse ? 'cronPulse 1.5s ease-in-out infinite' : 'none',
              }} />
              {/* Right bar segment */}
              {i < steps.length - 1 && (
                <div style={{
                  flex: 1, height: 3,
                  background: (isCompleted || i < currentIdx) ? T.success : T.bgInput,
                  borderRadius: i === steps.length - 2 ? '0 2px 2px 0' : 0,
                }} />
              )}
              {i === steps.length - 1 && <div style={{ flex: 1 }} />}
            </div>
            {/* Label */}
            <div style={{ fontSize: 9, color: labelColor, marginTop: 4, textAlign: 'center', whiteSpace: 'nowrap' }}>
              {step.label}
            </div>
          </div>
        );
      })}
      <style>{`@keyframes cronPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>
  );
}

function machineName(name: string | null | undefined, id: string | null | undefined): string {
  if (name) return name;
  if (id) return shortId(id);
  return '-';
}

function timeAgo(dateStr: string) {
  if (!dateStr) return '-';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function nextCronRun(schedule: string | null): string {
  if (!schedule) return '-';
  const match = schedule.match(/^\*\/(\d+) \* \* \* \*$/);
  if (!match) return schedule;
  const interval = parseInt(match[1]);
  const now = new Date();
  const mins = now.getMinutes();
  const nextMin = Math.ceil((mins + 1) / interval) * interval;
  const next = new Date(now);
  if (nextMin >= 60) {
    next.setHours(next.getHours() + 1);
    next.setMinutes(nextMin - 60, 0, 0);
  } else {
    next.setMinutes(nextMin, 0, 0);
  }
  return next.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function CronInfoBar({ queueKey, config, onPauseToggle, onRun }: {
  queueKey: string;
  config: CronConfigItem | undefined;
  onPauseToggle: (queue: string, paused: boolean) => void;
  onRun: (queue: string) => void;
}) {
  const [running, setRunning] = useState(false);
  const [pausing, setPausing] = useState(false);

  if (!config) return null;

  const handleRun = async () => {
    setRunning(true);
    try { await onRun(queueKey); } finally { setRunning(false); }
  };

  const handlePause = async () => {
    setPausing(true);
    try { await onPauseToggle(queueKey, !config.paused); } finally { setPausing(false); }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '6px 16px',
      background: T.bgSecondary, fontSize: 11, color: T.textMuted, borderBottom: `1px solid ${T.border}`,
    }}>
      {/* Clock icon */}
      <span style={{ fontSize: 12 }}>&#9201;</span>

      <span>Cron: <span style={{ color: T.textSecondary }}>{config.label}</span></span>

      {config.schedule && (
        <span>Proximo: <span style={{ color: T.textSecondary }}>{nextCronRun(config.schedule)}</span></span>
      )}

      {config.paused && (
        <span style={{
          padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
          background: T.warningBg, color: T.warning,
        }}>
          Pausado
        </span>
      )}

      <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
        {/* Pause/Resume button — only for cron-enabled queues */}
        {config.enabled && (
          <Button
            variant="ghost" size="xs"
            onClick={(e: any) => { e.stopPropagation(); handlePause(); }}
            disabled={pausing}
            style={{ color: config.paused ? T.warning : T.textMuted, background: config.paused ? T.warningBg : 'transparent' }}
          >
            {pausing ? '...' : config.paused ? 'Retomar' : 'Pausar'}
          </Button>
        )}

        {/* Run Now button */}
        <Button
          variant="secondary" size="xs"
          onClick={(e: any) => { e.stopPropagation(); handleRun(); }}
          disabled={running}
          style={{ background: T.primaryBg, color: T.primary }}
        >
          {running ? '⟳ Rodando...' : 'Rodar agora'}
        </Button>
      </div>
    </div>
  );
}

const thStyle: any = { textAlign: 'left', padding: '8px 4px', fontWeight: 600, whiteSpace: 'nowrap' };
const tdStyle: any = { padding: '6px 4px', whiteSpace: 'nowrap' };
const tdMono: any = { ...tdStyle, fontFamily: 'monospace', color: T.textSecondary };
const tdGray: any = { ...tdStyle, color: T.textMuted };
const tdErr: any = { ...tdStyle, color: T.danger, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11 };
const tdClient: any = { ...tdStyle, color: T.textSecondary, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' };

function CancelBtn({ status, onClick }: { status: string; onClick: () => void }) {
  if (!['pending', 'processing', 'running'].includes(status)) return null;
  return (
    <Button variant="danger" size="xs" onClick={(e: any) => { e.stopPropagation(); onClick(); }} style={{ background: T.dangerBg }}>Cancelar</Button>
  );
}

function QueueTable({ queueKey, items, onCancel }: { queueKey: string; items: any[]; onCancel: (id: string) => void }) {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const toggleRow = (id: string) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  const steps = QUEUE_STEPS[queueKey] || BACKUP_STEPS;
  const renderHead = () => {
    switch (queueKey) {
      case 'installations':
        return <tr style={{ color: T.textMuted, borderBottom: `1px solid ${T.border}` }}>
          <th style={thStyle}>ID</th><th style={thStyle}>Status</th><th style={thStyle}>Cliente</th>
          <th style={thStyle}>Servidor</th><th style={thStyle}>Agente</th><th style={thStyle}>Modelo</th>
          <th style={thStyle}>Retry</th><th style={thStyle}>Criado</th><th style={thStyle}>Atualizado</th>
          <th style={thStyle}>Erro</th><th style={thStyle}></th>
        </tr>;
      case 'reinstallations':
        return <tr style={{ color: T.textMuted, borderBottom: `1px solid ${T.border}` }}>
          <th style={thStyle}>ID</th><th style={thStyle}>Status</th><th style={thStyle}>Cliente</th>
          <th style={thStyle}>Máquina</th><th style={thStyle}>Agente</th><th style={thStyle}>Modelo</th>
          <th style={thStyle}>Retry</th><th style={thStyle}>Criado</th><th style={thStyle}>Atualizado</th>
          <th style={thStyle}>Erro</th><th style={thStyle}></th>
        </tr>;
      case 'backups':
        return <tr style={{ color: T.textMuted, borderBottom: `1px solid ${T.border}` }}>
          <th style={thStyle}>ID</th><th style={thStyle}>Status</th><th style={thStyle}>Cliente</th>
          <th style={thStyle}>Máquina</th><th style={thStyle}>Tipo</th><th style={thStyle}>Tamanho</th>
          <th style={thStyle}>Criado</th><th style={thStyle}>Atualizado</th>
          <th style={thStyle}>Erro</th><th style={thStyle}></th>
        </tr>;
      case 'agent_creation':
        return <tr style={{ color: T.textMuted, borderBottom: `1px solid ${T.border}` }}>
          <th style={thStyle}>ID</th><th style={thStyle}>Status</th><th style={thStyle}>Cliente</th>
          <th style={thStyle}>Máquina</th><th style={thStyle}>Agente</th><th style={thStyle}>Modelo</th>
          <th style={thStyle}>Etapa</th><th style={thStyle}>Criado</th><th style={thStyle}>Atualizado</th>
          <th style={thStyle}>Erro</th><th style={thStyle}></th>
        </tr>;
      case 'credential_import':
        return <tr style={{ color: T.textMuted, borderBottom: `1px solid ${T.border}` }}>
          <th style={thStyle}>ID</th><th style={thStyle}>Status</th><th style={thStyle}>Cliente</th>
          <th style={thStyle}>Origem</th><th style={thStyle}>Destino</th><th style={thStyle}>Integrações</th>
          <th style={thStyle}>Criado</th><th style={thStyle}>Processado</th><th style={thStyle}></th>
        </tr>;
      case 'skill_install':
        return <tr style={{ color: T.textMuted, borderBottom: `1px solid ${T.border}` }}>
          <th style={thStyle}>ID</th><th style={thStyle}>Status</th><th style={thStyle}>Cliente</th>
          <th style={thStyle}>Máquina</th><th style={thStyle}>Skill</th><th style={thStyle}>Criado</th>
          <th style={thStyle}>Atualizado</th><th style={thStyle}>Erro</th><th style={thStyle}></th>
        </tr>;
      case 'machine_actions':
      case 'brain_saves':
        return <tr style={{ color: T.textMuted, borderBottom: `1px solid ${T.border}` }}>
          <th style={thStyle}>ID</th><th style={thStyle}>Status</th><th style={thStyle}>Cliente</th>
          <th style={thStyle}>Máquina</th><th style={thStyle}>Ação</th><th style={thStyle}>Fonte</th>
          <th style={thStyle}>Retry</th><th style={thStyle}>Criado</th><th style={thStyle}>Atualizado</th>
          <th style={thStyle}>Erro</th><th style={thStyle}></th>
        </tr>;
      case 'credential_saves':
        return <tr style={{ color: T.textMuted, borderBottom: `1px solid ${T.border}` }}>
          <th style={thStyle}>ID</th><th style={thStyle}>Status</th><th style={thStyle}>Cliente</th>
          <th style={thStyle}>Máquina</th><th style={thStyle}>Provider</th><th style={thStyle}>Target</th>
          <th style={thStyle}>Fonte</th><th style={thStyle}>Criado</th><th style={thStyle}>Atualizado</th>
        </tr>;
      default:
        return null;
    }
  };

  const renderRow = (item: any) => {
    const cancel = <CancelBtn status={item.status} onClick={() => onCancel(item.id)} />;
    const isExp = expandedRows[item.id];
    const base = { borderBottom: '1px solid #2A2A2A', cursor: 'pointer', transition: 'background 0.15s' } as any;
    const currentStep = getStepForItem(queueKey, item);
    const errorMsg = item.error_message || item.last_error || '';
    const stageMsg = item.stage_message || '';

    const colCount = (queueKey === 'credential_import' || queueKey === 'skill_install') ? 9 : 11;

    // Build all detail fields for the expanded view
    const detailFields: { label: string; value: string; color?: string }[] = [];
    detailFields.push({ label: 'ID Completo', value: item.id, color: T.textSecondary });
    if (item.customer_email) detailFields.push({ label: 'Cliente', value: item.customer_email });
    if (item.customer_id) detailFields.push({ label: 'Customer ID', value: item.customer_id, color: T.textMuted });
    if (item.server_name) detailFields.push({ label: 'Servidor', value: item.server_name });
    if (item.server_id) detailFields.push({ label: 'Server ID', value: item.server_id, color: T.textMuted });
    if (item.machine_name) detailFields.push({ label: 'Máquina', value: item.machine_name });
    if (item.machine_id) detailFields.push({ label: 'Machine ID', value: item.machine_id, color: T.textMuted });
    if (item.agent_name) detailFields.push({ label: 'Agente', value: item.agent_name });
    if (item.llm_model) detailFields.push({ label: 'Modelo', value: item.llm_model });
    if (item.model) detailFields.push({ label: 'Modelo', value: item.model });
    if (item.language) detailFields.push({ label: 'Idioma', value: item.language });
    if (item.brain_id) detailFields.push({ label: 'Cérebro', value: item.brain_id });
    if (item.backup_type) detailFields.push({ label: 'Tipo Backup', value: item.backup_type });
    if (item.file_size) detailFields.push({ label: 'Tamanho', value: formatFileSize(item.file_size) });
    if (item.download_url) detailFields.push({ label: 'Download', value: item.download_url, color: T.primary });
    if (item.email) detailFields.push({ label: 'Email', value: item.email });
    if (item.source_machine_id) detailFields.push({ label: 'Origem ID', value: item.source_machine_id, color: T.textMuted });
    if (item.source_machine_name) detailFields.push({ label: 'Origem', value: item.source_machine_name });
    if (item.target_machine_id) detailFields.push({ label: 'Destino ID', value: item.target_machine_id, color: T.textMuted });
    if (item.target_machine_name) detailFields.push({ label: 'Destino', value: item.target_machine_name });
    if (item.source_type) detailFields.push({ label: 'Tipo Fonte', value: item.source_type });
    if (item.retry_count !== undefined) detailFields.push({ label: 'Tentativas', value: `${item.retry_count}/${item.max_retries || 3}` });
    if (item.retries !== undefined) detailFields.push({ label: 'Tentativas', value: `${item.retries}/${item.max_retries || 3}` });
    if (item.action) detailFields.push({ label: 'Ação', value: item.action, color: T.primary });
    if (item.source) detailFields.push({ label: 'Fonte', value: item.source });
    if (item.source_detail) detailFields.push({ label: 'Detalhe Fonte', value: item.source_detail });
    if (item.runner_id) detailFields.push({ label: 'Runner', value: item.runner_id, color: T.textMuted });
    if (item.dedup_key) detailFields.push({ label: 'Dedup Key', value: item.dedup_key, color: T.textMuted });
    if (item.expires_at) detailFields.push({ label: 'Expira em', value: new Date(item.expires_at).toLocaleString('pt-BR') });
    if (item.payload && item.payload !== '{}') { try { detailFields.push({ label: 'Payload', value: JSON.stringify(JSON.parse(item.payload), null, 2) }); } catch {} }
    if (item.installation_stage) detailFields.push({ label: 'Stage', value: item.installation_stage, color: T.purple });
    if (item.stage) detailFields.push({ label: 'Stage', value: item.stage, color: T.purple });
    if (item.telegram_token) detailFields.push({ label: 'Telegram Token', value: '••••' + item.telegram_token.slice(-6) });
    if (item.created_at) detailFields.push({ label: 'Criado', value: new Date(item.created_at).toLocaleString('pt-BR') });
    if (item.updated_at) detailFields.push({ label: 'Atualizado', value: new Date(item.updated_at).toLocaleString('pt-BR') });
    if (item.started_at) detailFields.push({ label: 'Iniciado', value: new Date(item.started_at).toLocaleString('pt-BR') });
    if (item.completed_at) detailFields.push({ label: 'Concluído', value: new Date(item.completed_at).toLocaleString('pt-BR') });
    if (item.processed_at) detailFields.push({ label: 'Processado', value: new Date(item.processed_at).toLocaleString('pt-BR') });

    // Parse JSON fields
    let skills: string[] = [];
    try { skills = JSON.parse(item.skills || '[]'); } catch {}
    if (skills.length > 0) detailFields.push({ label: 'Skills', value: skills.join(', ') });

    let integrations: string[] = [];
    try { integrations = JSON.parse(item.integrations || '[]'); } catch {}
    if (integrations.length > 0) detailFields.push({ label: 'Integrações', value: integrations.join(', ') });

    let result: any = null;
    try { result = JSON.parse(item.result || 'null'); } catch {}

    let logs: string[] = [];
    try { logs = JSON.parse(item.installation_logs || '[]'); } catch {}

    const detailRow = isExp ? (
      <tr key={`${item.id}-detail`} style={{ background: T.bg }}>
        <td colSpan={colCount} style={{ padding: '12px 16px 16px' }}>
          <ProgressBar steps={steps} currentStep={currentStep} status={item.status} />

          {/* Detail fields grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '4px 24px', marginTop: 10 }}>
            {detailFields.map((f, i) => (
              <div key={i} style={{ fontSize: 11, padding: '2px 0', display: 'flex', gap: 6 }}>
                <span style={{ color: T.textMuted, minWidth: 90, flexShrink: 0 }}>{f.label}:</span>
                <span style={{ color: f.color || T.textSecondary, wordBreak: 'break-all' }}>{f.value}</span>
              </div>
            ))}
          </div>

          {/* Error */}
          {errorMsg && (
            <div style={{ fontSize: 11, color: T.danger, marginTop: 8, padding: '6px 10px', background: T.dangerBg, borderRadius: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', border: `1px solid ${T.dangerBg}` }}>
              <strong>Erro:</strong> {errorMsg}
            </div>
          )}

          {/* Stage message */}
          {stageMsg && (
            <div style={{ fontSize: 11, color: T.purple, marginTop: 4, padding: '4px 10px', background: 'rgba(167,139,250,0.08)', borderRadius: 6 }}>
              <strong>Stage:</strong> {stageMsg}
            </div>
          )}

          {/* Result (credential import) */}
          {result && (
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4, padding: '4px 10px', background: T.bgCard, borderRadius: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              <strong>Resultado:</strong> {JSON.stringify(result, null, 2)}
            </div>
          )}

          {/* Installation logs */}
          {logs.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, marginBottom: 3 }}>LOGS ({logs.length})</div>
              <div style={{ fontSize: 10, color: T.textSecondary, background: T.bgSecondary, borderRadius: 6, padding: '6px 10px', maxHeight: 200, overflowY: 'auto', fontFamily: 'monospace', lineHeight: '1.6', border: '1px solid #21262D' }}>
                {logs.map((log: string, i: number) => (
                  <div key={i}>{log}</div>
                ))}
              </div>
            </div>
          )}
        </td>
      </tr>
    ) : null;

    const rowClick = () => toggleRow(item.id);

    switch (queueKey) {
      case 'installations':
        return [<tr key={item.id} style={base} onClick={rowClick}>
          <td style={tdMono}>{shortId(item.id)}</td>
          <td style={tdStyle}><StatusBadge status={item.status} /></td>
          <td style={tdClient}>{item.customer_email || shortId(item.customer_id)}</td>
          <td style={tdStyle}><span style={{ color: T.textSecondary }}>{item.server_name || shortId(item.server_id)}</span></td>
          <td style={tdStyle}><span style={{ color: T.textSecondary }}>{item.agent_name || '-'}</span></td>
          <td style={tdGray}>{item.llm_model || '-'}</td>
          <td style={tdGray}>{retryLabel(item.retry_count, item.max_retries)}</td>
          <td style={tdGray}>{timeAgo(item.created_at)}</td>
          <td style={tdGray}>{timeAgo(item.updated_at)}</td>
          <td style={tdErr}>{errorMsg || '-'}</td>
          <td style={tdStyle}>{cancel}</td>
        </tr>, detailRow];
      case 'reinstallations':
        return [<tr key={item.id} style={base} onClick={rowClick}>
          <td style={tdMono}>{shortId(item.id)}</td>
          <td style={tdStyle}><StatusBadge status={item.status} /></td>
          <td style={tdClient}>{item.customer_email || shortId(item.customer_id)}</td>
          <td style={tdStyle}><span style={{ color: T.textSecondary }}>{machineName(item.machine_name, item.machine_id)}</span></td>
          <td style={tdStyle}><span style={{ color: T.textSecondary }}>{item.agent_name || '-'}</span></td>
          <td style={tdGray}>{item.llm_model || '-'}</td>
          <td style={tdGray}>{retryLabel(item.retry_count, item.max_retries)}</td>
          <td style={tdGray}>{timeAgo(item.created_at)}</td>
          <td style={tdGray}>{timeAgo(item.updated_at)}</td>
          <td style={tdErr}>{errorMsg || '-'}</td>
          <td style={tdStyle}>{cancel}</td>
        </tr>, detailRow];
      case 'backups':
        return [<tr key={item.id} style={base} onClick={rowClick}>
          <td style={tdMono}>{shortId(item.id)}</td>
          <td style={tdStyle}><StatusBadge status={item.status} /></td>
          <td style={tdClient}>{item.customer_email || shortId(item.customer_id)}</td>
          <td style={tdStyle}><span style={{ color: T.textSecondary }}>{machineName(item.machine_name, item.machine_id)}</span></td>
          <td style={tdGray}>{item.backup_type || '-'}</td>
          <td style={tdGray}>{formatFileSize(item.file_size)}</td>
          <td style={tdGray}>{timeAgo(item.created_at)}</td>
          <td style={tdGray}>{timeAgo(item.updated_at)}</td>
          <td style={tdErr}>{errorMsg || '-'}</td>
          <td style={tdStyle}>{cancel}</td>
        </tr>, detailRow];
      case 'agent_creation':
        return [<tr key={item.id} style={base} onClick={rowClick}>
          <td style={tdMono}>{shortId(item.id)}</td>
          <td style={tdStyle}><StatusBadge status={item.status} /></td>
          <td style={tdClient}>{item.customer_email || shortId(item.customer_id)}</td>
          <td style={tdStyle}><span style={{ color: T.textSecondary }}>{machineName(item.machine_name, item.machine_id)}</span></td>
          <td style={tdStyle}><span style={{ color: T.textSecondary }}>{item.agent_name || '-'}</span></td>
          <td style={tdGray}>{item.model || '-'}</td>
          <td style={tdStyle}><StageBadge stage={item.stage} /></td>
          <td style={tdGray}>{timeAgo(item.created_at)}</td>
          <td style={tdGray}>{timeAgo(item.updated_at)}</td>
          <td style={tdErr}>{errorMsg || stageMsg || '-'}</td>
          <td style={tdStyle}>{cancel}</td>
        </tr>, detailRow];
      case 'credential_import':
        return [<tr key={item.id} style={base} onClick={rowClick}>
          <td style={tdMono}>{shortId(item.id)}</td>
          <td style={tdStyle}><StatusBadge status={item.status} /></td>
          <td style={tdClient}>{item.customer_email || shortId(item.customer_id)}</td>
          <td style={tdStyle}><span style={{ color: T.textSecondary }}>{machineName(item.source_machine_name, item.source_machine_id)}</span></td>
          <td style={tdStyle}><span style={{ color: T.textSecondary }}>{machineName(item.target_machine_name, item.target_machine_id)}</span></td>
          <td style={tdGray}>{parseIntegrationsCount(item.integrations)}</td>
          <td style={tdGray}>{timeAgo(item.created_at)}</td>
          <td style={tdGray}>{timeAgo(item.processed_at)}</td>
          <td style={tdStyle}>{cancel}</td>
        </tr>, detailRow];
      case 'skill_install':
        return [<tr key={item.id} style={base} onClick={rowClick}>
          <td style={tdMono}>{shortId(item.id)}</td>
          <td style={tdStyle}><StatusBadge status={item.status} /></td>
          <td style={tdClient}>{item.customer_email || shortId(item.customer_id)}</td>
          <td style={tdStyle}><span style={{ color: T.textSecondary }}>{machineName(item.machine_name, item.machine_id)}</span></td>
          <td style={tdStyle}><span style={{ color: T.primary }}>{item.skill_name || '-'}</span></td>
          <td style={tdGray}>{timeAgo(item.created_at)}</td>
          <td style={tdGray}>{timeAgo(item.updated_at)}</td>
          <td style={tdErr}>{errorMsg || '-'}</td>
          <td style={tdStyle}>{cancel}</td>
        </tr>, detailRow];
      case 'machine_actions':
      case 'brain_saves':
        return [<tr key={item.id} style={base} onClick={rowClick}>
          <td style={tdMono}>{shortId(item.id)}</td>
          <td style={tdStyle}><StatusBadge status={item.status} /></td>
          <td style={tdClient}>{item.customer_email || shortId(item.customer_id)}</td>
          <td style={tdStyle}><span style={{ color: T.textSecondary }}>{machineName(item.machine_name, item.machine_id)}</span></td>
          <td style={tdStyle}><span style={{ color: T.primary, fontWeight: 600 }}>{queueKey === 'brain_saves' ? (item.payload ? (JSON.parse(item.payload || '{}').file || item.action) : item.action) : (item.action || '-')}</span></td>
          <td style={tdGray}>{item.source || '-'}</td>
          <td style={tdGray}>{`${item.retries || 0}/${item.max_retries || 3}`}</td>
          <td style={tdGray}>{timeAgo(item.created_at)}</td>
          <td style={tdGray}>{timeAgo(item.completed_at || item.updated_at)}</td>
          <td style={tdErr}>{item.error || '-'}</td>
          <td style={tdStyle}>{cancel}</td>
        </tr>, detailRow];
      case 'credential_saves':
        return [<tr key={item.id} style={base} onClick={rowClick}>
          <td style={tdMono}>{shortId(item.id)}</td>
          <td style={tdStyle}><StatusBadge status={item.status} /></td>
          <td style={tdClient}>{item.customer_email || shortId(item.customer_id)}</td>
          <td style={tdStyle}><span style={{ color: T.textSecondary }}>{item.machine_id ? machineName(item.machine_name, item.machine_id) : '-'}</span></td>
          <td style={tdStyle}><span style={{ color: T.accent }}>{item.provider}</span></td>
          <td style={tdStyle}>{item.target_type}</td>
          <td style={tdGray}>{item.source || '-'}</td>
          <td style={tdGray}>{timeAgo(item.created_at)}</td>
          <td style={tdGray}>{timeAgo(item.updated_at)}</td>
        </tr>, detailRow];
      default:
        return null;
    }
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 700 }}>
      <thead>{renderHead()}</thead>
      <tbody>{items.map(renderRow).flat().filter(Boolean)}</tbody>
    </table>
  );
}

// ── System Crons Types ──
interface SystemCron {
  id: string;
  name: string;
  schedule: string;
  schedule_label: string;
  category: string;
  description: string;
  last_execution: { status: string; started_at: string; completed_at: string; duration_ms: number; error: string } | null;
  stats_24h: { total: number; errors: number; avg_duration: number } | null;
}

interface CronStats {
  active_crons: number;
  total_runs_24h: number;
  errors_24h: number;
  avg_duration_ms: number;
  last_execution: string | null;
}

interface CronExecution {
  id: string;
  cron_id: string;
  cron_name: string;
  category: string;
  status: string;
  started_at: string;
  completed_at: string;
  duration_ms: number;
  result: string;
  error: string;
}

function CronStatusDot({ status }: { status: string | undefined }) {
  const color = status === 'completed' ? T.success : status === 'failed' ? T.danger : status === 'running' ? T.primary : T.textMuted;
  const pulse = status === 'running';
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color,
      boxShadow: pulse ? `0 0 6px ${color}` : 'none',
      animation: pulse ? 'cronPulse 1.5s ease-in-out infinite' : 'none',
    }} />
  );
}

function formatDuration(ms: number | null | undefined): string {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}min`;
}

export function AdminCronJobs() {
  const [data, setData] = useState<QueueStatus | null>(null);
  const [cronConfig, setCronConfig] = useState<CronConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({ installations: true });
  const [lastUpdate, setLastUpdate] = useState(0);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [runResult, setRunResult] = useState<{ queue: string; message: string } | null>(null);
  const timerRef = useRef<any>(null);

  // New state for system crons
  const [systemCrons, setSystemCrons] = useState<SystemCron[]>([]);
  const [cronStats, setCronStats] = useState<CronStats | null>(null);
  const [execHistory, setExecHistory] = useState<CronExecution[]>([]);
  const [expandedCron, setExpandedCron] = useState<Record<string, boolean>>({});
  const [cronHistory, setCronHistory] = useState<Record<string, CronExecution[]>>({});
  const [historyFilter, setHistoryFilter] = useState<{ cron_id: string; status: string }>({ cron_id: '', status: '' });
  const [workersOpen, setWorkersOpen] = useState(true);

  const fetchData = async () => {
    try {
      const res = await api.get('/admin/cronjobs/queue-status') as any;
      setData(res);
      setLastUpdate(Date.now());
      setSecondsAgo(0);
      setError('');
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  };

  const fetchCronConfig = async () => {
    try {
      const res = await api.get('/admin/cronjobs/cron-config') as any;
      setCronConfig(res);
    } catch (e: any) {
      console.error('Erro ao carregar cron config:', e);
    }
  };

  const fetchSystemCrons = async () => {
    try {
      const res = await api.get('/admin/cronjobs/system-crons') as any;
      setSystemCrons(res.crons || []);
    } catch {}
  };

  const fetchCronStats = async () => {
    try {
      const res = await api.get('/admin/cronjobs/stats') as any;
      setCronStats(res);
    } catch {}
  };

  const fetchExecHistory = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (historyFilter.cron_id) params.set('cron_id', historyFilter.cron_id);
      if (historyFilter.status) params.set('status', historyFilter.status);
      params.set('limit', '50');
      const res = await api.get(`/admin/cronjobs/execution-history?${params}`) as any;
      setExecHistory(res.executions || []);
    } catch {}
  }, [historyFilter]);

  const fetchCronDetail = async (cronId: string) => {
    try {
      const res = await api.get(`/admin/cronjobs/execution-history?cron_id=${cronId}&limit=10`) as any;
      setCronHistory(prev => ({ ...prev, [cronId]: res.executions || [] }));
    } catch {}
  };

  useEffect(() => {
    fetchData();
    fetchCronConfig();
    fetchSystemCrons();
    fetchCronStats();
    fetchExecHistory();
    const interval = setInterval(() => { fetchData(); fetchSystemCrons(); fetchCronStats(); fetchExecHistory(); }, 30000);
    timerRef.current = setInterval(() => setSecondsAgo(s => s + 1), 1000);
    return () => { clearInterval(interval); clearInterval(timerRef.current); };
  }, []);

  useEffect(() => { fetchExecHistory(); }, [historyFilter]);

  const toggle = (key: string) => setExpanded(e => ({ ...e, [key]: !e[key] }));

  const handlePauseToggle = async (queue: string, paused: boolean) => {
    try {
      await api.post(`/admin/cronjobs/pause/${queue}`, { paused });
      setCronConfig(prev => prev ? { ...prev, [queue]: { ...prev[queue], paused } } : prev);
    } catch (e: any) {
      console.error('Erro ao pausar/retomar:', e);
    }
  };

  const handleRun = async (queue: string) => {
    try {
      const res = await api.post(`/admin/cronjobs/run/${queue}`) as any;
      setRunResult({ queue, message: res.message || 'Executado com sucesso' });
      setTimeout(() => setRunResult(null), 4000);
      // Refresh data after run
      fetchData();
    } catch (e: any) {
      setRunResult({ queue, message: `Erro: ${e.message}` });
      setTimeout(() => setRunResult(null), 4000);
    }
  };

  const cardStyle = (borderColor: string): any => ({
    flex: '1 1 200px', padding: '16px 20px', borderRadius: 12,
    background: T.bgModal, border: `1px solid ${borderColor}33`,
    minWidth: 160,
  });

  if (loading) return <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>;
  if (error) return <div style={{ padding: 40, color: T.danger }}>Erro: {error}</div>;
  if (!data) return null;

  const s = data.summary;
  const kpiCard = (borderColor: string): any => ({
    flex: '1 1 160px', padding: '16px 20px', borderRadius: 12,
    background: 'rgba(22,27,34,0.8)', backdropFilter: 'blur(12px)',
    border: `1px solid ${borderColor}33`, minWidth: 140,
  });

  const uptimePercent = cronStats && cronStats.total_runs_24h > 0
    ? Math.round(((cronStats.total_runs_24h - cronStats.errors_24h) / cronStats.total_runs_24h) * 100)
    : 100;

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200, color: T.text }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: T.text }}>Central de Cron Jobs</h1>
        <p style={{ fontSize: 13, color: T.textMuted, margin: '4px 0 0' }}>
          Monitoramento completo de crons, filas e execuções do sistema
        </p>
        <div style={{ fontSize: 11, color: T.textDisabled, marginTop: 8 }}>
          Atualizado há {secondsAgo}s
        </div>
      </div>

      {/* Run result toast */}
      {runResult && (
        <div style={{
          padding: '8px 16px', marginBottom: 12, borderRadius: 8, fontSize: 12,
          background: T.primaryBg, border: `1px solid ${T.primary}44`, color: T.primary,
        }}>
          [{QUEUE_CONFIG.find(q => q.key === runResult.queue)?.label || runResult.queue}] {runResult.message}
        </div>
      )}

      {/* ═══ SEÇÃO 1: KPI Cards ═══ */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 28, flexWrap: 'wrap' }}>
        <div style={kpiCard(T.primary)}>
          <div style={{ fontSize: 11, color: T.primary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Crons Ativos</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: T.primary, marginTop: 4 }}>{cronStats?.active_crons || 0}</div>
        </div>
        <div style={kpiCard(T.success)}>
          <div style={{ fontSize: 11, color: T.success, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Execuções 24h</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: T.success, marginTop: 4 }}>{cronStats?.total_runs_24h || 0}</div>
        </div>
        <div style={kpiCard(T.danger)}>
          <div style={{ fontSize: 11, color: T.danger, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Erros 24h</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: T.danger, marginTop: 4 }}>{cronStats?.errors_24h || 0}</div>
        </div>
        <div style={kpiCard(T.purple)}>
          <div style={{ fontSize: 11, color: T.purple, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Tempo Médio</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: T.purple, marginTop: 4 }}>{formatDuration(cronStats?.avg_duration_ms)}</div>
        </div>
        <div style={kpiCard(T.textMuted)}>
          <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Última Execução</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: T.textSecondary, marginTop: 4 }}>{cronStats?.last_execution ? timeAgo(cronStats.last_execution) : '-'}</div>
        </div>
        <div style={kpiCard(uptimePercent >= 95 ? T.success : T.warning)}>
          <div style={{ fontSize: 11, color: uptimePercent >= 95 ? T.success : T.warning, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Uptime</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: uptimePercent >= 95 ? T.success : T.warning, marginTop: 4 }}>{uptimePercent}%</div>
        </div>
      </div>

      {/* ═══ SEÇÃO 2: Crons do Sistema ═══ */}
      <div style={{ marginBottom: 28 }}>
        <div
          onClick={() => setWorkersOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', padding: '12px 16px', cursor: 'pointer',
            background: T.bgModal, borderRadius: workersOpen ? '10px 10px 0 0' : 10,
            border: '1px solid #21262D', userSelect: 'none',
          }}
        >
          <span style={{ fontSize: 10, marginRight: 8, color: T.textMuted, transition: 'transform 0.2s', transform: workersOpen ? 'rotate(90deg)' : 'rotate(0)' }}>&#9654;</span>
          <span style={{ fontSize: 16, marginRight: 8 }}></span>
          <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>Cloudflare Workers</span>
          <span style={{ fontSize: 11, color: T.textMuted }}>{systemCrons.length} crons</span>
        </div>
        {workersOpen && (
          <div style={{ border: '1px solid #21262D', borderTop: 'none', borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
            {systemCrons.map((cron) => {
              const isExp = expandedCron[cron.id];
              const lastStatus = cron.last_execution?.status;
              return (
                <div key={cron.id} style={{ borderBottom: '1px solid #2A2A2A' }}>
                  <div
                    onClick={() => {
                      setExpandedCron(prev => ({ ...prev, [cron.id]: !prev[cron.id] }));
                      if (!cronHistory[cron.id]) fetchCronDetail(cron.id);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                      cursor: 'pointer', background: T.bgSecondary, transition: 'background 0.15s',
                    }}
                  >
                    <CronStatusDot status={lastStatus} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{cron.name}</div>
                      <div style={{ fontSize: 11, color: T.textMuted }}>{cron.description}</div>
                    </div>
                    <div style={{ textAlign: 'right', minWidth: 100 }}>
                      <div style={{ fontSize: 11, color: T.textSecondary, fontFamily: 'monospace' }}>{cron.schedule}</div>
                      <div style={{ fontSize: 10, color: T.textMuted }}>{cron.schedule_label}</div>
                    </div>
                    <div style={{ textAlign: 'right', minWidth: 80 }}>
                      <div style={{ fontSize: 11, color: T.textSecondary }}>
                        {cron.last_execution ? timeAgo(cron.last_execution.started_at) : '-'}
                      </div>
                      <div style={{ fontSize: 10, color: T.textMuted }}>
                        {cron.last_execution ? formatDuration(cron.last_execution.duration_ms) : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center', minWidth: 60 }}>
                      <div style={{ fontSize: 11, color: T.success }}>{cron.stats_24h?.total || 0} runs</div>
                      {(cron.stats_24h?.errors || 0) > 0 && (
                        <div style={{ fontSize: 10, color: T.danger }}>{cron.stats_24h?.errors} erros</div>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: T.textMuted, minWidth: 50, textAlign: 'right' }}>
                      {formatDuration(cron.stats_24h?.avg_duration)}
                    </div>
                  </div>
                  {isExp && (
                    <div style={{ padding: '8px 16px 12px', background: T.bg }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 6 }}>Últimas Execuções</div>
                      {(cronHistory[cron.id] || []).length === 0 ? (
                        <div style={{ fontSize: 11, color: T.textDisabled }}>Sem registros ainda</div>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                          <thead>
                            <tr style={{ color: T.textMuted }}>
                              <th style={{ textAlign: 'left', padding: '4px 8px' }}>Hora</th>
                              <th style={{ textAlign: 'left', padding: '4px 8px' }}>Status</th>
                              <th style={{ textAlign: 'left', padding: '4px 8px' }}>Duração</th>
                              <th style={{ textAlign: 'left', padding: '4px 8px' }}>Erro</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(cronHistory[cron.id] || []).map((exec) => (
                              <tr key={exec.id} style={{ borderTop: '1px solid #2A2A2A' }}>
                                <td style={{ padding: '4px 8px', color: T.textSecondary }}>
                                  {exec.started_at ? new Date(exec.started_at + 'Z').toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit', day: '2-digit', month: '2-digit' }) : '-'}
                                </td>
                                <td style={{ padding: '4px 8px' }}><StatusBadge status={exec.status} /></td>
                                <td style={{ padding: '4px 8px', color: T.textSecondary }}>{formatDuration(exec.duration_ms)}</td>
                                <td style={{ padding: '4px 8px', color: T.danger, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>{exec.error || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══ SEÇÃO 3: Histórico de Execuções ═══ */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: T.text }}>Histórico de Execuções</h2>
          <select
            value={historyFilter.cron_id}
            onChange={(e: any) => setHistoryFilter(prev => ({ ...prev, cron_id: e.target.value }))}
            style={{ padding: '4px 8px', borderRadius: 6, fontSize: 11, background: T.bgModal, border: '1px solid #21262D', color: T.textSecondary }}
          >
            <option value="">Todos os crons</option>
            {systemCrons.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            value={historyFilter.status}
            onChange={(e: any) => setHistoryFilter(prev => ({ ...prev, status: e.target.value }))}
            style={{ padding: '4px 8px', borderRadius: 6, fontSize: 11, background: T.bgModal, border: '1px solid #21262D', color: T.textSecondary }}
          >
            <option value="">Todos status</option>
            <option value="completed">Completo</option>
            <option value="failed">Falha</option>
            <option value="running">Rodando</option>
          </select>
        </div>
        <div style={{ borderRadius: 10, border: '1px solid #21262D', overflow: 'hidden', background: T.bgSecondary }}>
          {execHistory.length === 0 ? (
            <div style={{ padding: 16, fontSize: 12, color: T.textDisabled }}>Sem execuções registradas</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ color: T.textMuted, borderBottom: `1px solid ${T.border}` }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Hora</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Cron</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Duração</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Erro</th>
                </tr>
              </thead>
              <tbody>
                {execHistory.map((exec) => (
                  <tr key={exec.id} style={{ borderBottom: '1px solid #2A2A2A' }}>
                    <td style={{ padding: '6px 12px', color: T.textSecondary, whiteSpace: 'nowrap' }}>
                      {exec.started_at ? new Date(exec.started_at + 'Z').toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}
                    </td>
                    <td style={{ padding: '6px 12px', color: T.text, fontWeight: 600 }}>{exec.cron_name}</td>
                    <td style={{ padding: '6px 12px' }}><StatusBadge status={exec.status} /></td>
                    <td style={{ padding: '6px 12px', color: T.textSecondary }}>{formatDuration(exec.duration_ms)}</td>
                    <td style={{ padding: '6px 12px', color: T.danger, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11 }}>{exec.error || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ═══ SEÇÃO 4: Filas de Processamento (EXISTENTE) ═══ */}
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px', color: T.text }}>Filas de Processamento</h2>
      </div>

      {/* Summary Cards (existing) */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 28, flexWrap: 'wrap' }}>
        <div style={cardStyle(T.warning)}>
          <div style={{ fontSize: 11, color: T.warning, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Pendentes</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: T.warning, marginTop: 4 }}>{s.total_pending}</div>
        </div>
        <div style={cardStyle(T.primary)}>
          <div style={{ fontSize: 11, color: T.primary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Processando</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: T.primary, marginTop: 4 }}>{s.total_processing}</div>
        </div>
        <div style={cardStyle(T.success)}>
          <div style={{ fontSize: 11, color: T.success, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Concluídos 24h</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: T.success, marginTop: 4 }}>{s.total_completed_24h}</div>
        </div>
        <div style={cardStyle(T.danger)}>
          <div style={{ fontSize: 11, color: T.danger, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Falhas 24h</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: T.danger, marginTop: 4 }}>{s.total_failed_24h}</div>
        </div>
      </div>

      {/* Queue Sections */}
      {QUEUE_CONFIG.map(({ key, label }) => {
        const q = data.queues[key];
        const isOpen = expanded[key];
        return (
          <div key={key} style={{ marginBottom: 12, borderRadius: 10, border: '1px solid #21262D', background: T.bgSecondary, overflow: 'hidden' }}>
            {/* Section Header */}
            <div
              onClick={() => toggle(key)}
              style={{
                display: 'flex', alignItems: 'center', padding: '12px 16px', cursor: 'pointer',
                background: T.bgModal, userSelect: 'none',
              }}
            >
              <span style={{ fontSize: 10, marginRight: 8, color: T.textMuted, transition: 'transform 0.2s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0)' }}>&#9654;</span>
              <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{label}</span>
              <CountBadge count={q.pending} color="#EAB308" />
              <CountBadge count={q.processing} color={T.primary} />
            </div>

            {/* Cron Info Bar */}
            {isOpen && (
              <CronInfoBar
                queueKey={key}
                config={cronConfig?.[key]}
                onPauseToggle={handlePauseToggle}
                onRun={handleRun}
              />
            )}

            {/* Section Content */}
            {isOpen && (
              <div style={{ padding: '0 16px 12px', overflowX: 'auto' }}>
                {q.recent.length === 0 ? (
                  <div style={{ padding: '16px 0', fontSize: 13, color: T.textDisabled }}>Nenhum registro recente</div>
                ) : (
                  <QueueTable queueKey={key} items={q.recent} onCancel={async (itemId: string) => {
                    if (!confirm('Cancelar esta solicitação?')) return;
                    try {
                      await api.post(`/admin/cronjobs/cancel/${key}/${itemId}`, {});
                      fetchData();
                    } catch (err: any) {
                      alert(err?.message || 'Erro ao cancelar');
                    }
                  }} />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
